import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useMemo } from 'react';
import { MenuItem, FormControl, InputLabel, Select as MUISelect, FormHelperText, Checkbox as MUICheckbox, ListSubheader, } from '@mui/material';
import { SelectIcon } from '../../Icons';
import { selectStyles as classes } from './selectStyles';
import { ClassNames } from '@emotion/react';
import { TooltipLabel } from '../TooltipLabel';
import { formatOptions } from './utils';
/**
 * A component that displays a list of options for the user to pick from.
 *
 * @example
 *<Select label='Example' id='id' options={[]} value='' />
 *
 */
export function Select({ native, label, required, id, value, placeholder, shrinkLabel, tooltipText, helperText, disabled, name, options, color, labelColor, borderColor, helperTextColor, onChange, hiddenLabel, onFocus, onOpen, onClose, onBlur, multiple, className, backgroundColor, weight, defaultValue, onClick, onKeyDown, onKeyUp, autoComplete, }) {
    const formattedOptions = useMemo(() => formatOptions(options), [options]);
    const hasNumberValues = formattedOptions.length && typeof formattedOptions[0].options[0].value === 'number' ? true : false;
    const groupedItems = useMemo(() => {
        const items = [];
        formattedOptions.forEach((optionGroup) => {
            if (native) {
                if (optionGroup.group) {
                    items.push(_jsx("optgroup", { label: optionGroup.group, children: optionGroup.options.map((option) => (_jsx("option", { disabled: option.disabled, value: option.value, css: option.disabled ? undefined : classes.nativeOption, children: option.content }, option.value))) }, optionGroup.group));
                }
                else {
                    items.push(...optionGroup.options.map((option) => (_jsx("option", { disabled: option.disabled, value: option.value, css: option.disabled ? undefined : classes.nativeOption, children: option.content }, option.value))));
                }
            }
            else {
                if (optionGroup.group) {
                    items.push(_jsx(ListSubheader, { disableSticky: true, css: classes.groupHeading, children: optionGroup.group }, optionGroup.group));
                }
                items.push(...optionGroup.options.map((option) => (_jsxs(MenuItem, { css: classes.menuItem, disabled: option.disabled, value: option.value, children: [multiple && value ? (_jsx(MUICheckbox, { "aria-hidden": true, css: classes.checkbox(), checked: value.includes(option.value) })) : null, option.content] }, option.value))));
            }
        });
        return items;
    }, [formattedOptions, native, multiple, value]);
    function handleChange(event) {
        if (onChange) {
            if (multiple && native) {
                const { options } = event.target;
                const nativeValue = [];
                for (let i = 0; i < options.length; i += 1) {
                    if (options[i].selected) {
                        const convertedValue = hasNumberValues ? parseInt(options[i].value) : options[i].value;
                        nativeValue.push(convertedValue);
                    }
                }
                onChange(nativeValue);
            }
            else if (!multiple && native) {
                const { value } = event.target;
                const convertedValue = hasNumberValues ? parseInt(value) : value;
                onChange(convertedValue);
            }
            else {
                const { value } = event.target;
                onChange(value);
            }
        }
    }
    return (_jsx(ClassNames, { children: ({ css }) => (_jsxs("div", { className: className, children: [_jsxs(FormControl, { fullWidth: true, css: classes.formControl({
                        color,
                        labelColor,
                        borderColor,
                        backgroundColor,
                    }), children: [shrinkLabel && !hiddenLabel ? (_jsx(InputLabel, { required: required, id: `${id}-input-label`, disabled: disabled, shrink: native && multiple ? true : undefined, htmlFor: id, children: label })) : null, _jsxs(MUISelect, { variant: 'outlined', labelId: `${id}-input-label`, id: id, value: value, defaultValue: defaultValue, label: shrinkLabel && !hiddenLabel ? label : undefined, onChange: handleChange, native: native, required: required, name: name, disabled: disabled, inputProps: {
                                'aria-label': hiddenLabel ? label : undefined,
                                'aria-describedby': helperText ? helperText : undefined,
                                autoComplete: autoComplete,
                            }, onOpen: onOpen, onFocus: onFocus, onClose: onClose, onBlur: onBlur, onClick: onClick, onKeyDown: onKeyDown, onKeyUp: onKeyUp, displayEmpty: !shrinkLabel || (shrinkLabel && hiddenLabel), multiple: multiple, renderValue: multiple && placeholder && value.length === 0 ? () => _jsx("em", { children: placeholder }) : undefined, classes: {
                                multiple: css(classes.removeCheckbox),
                                icon: css(classes.selectIcon(color)),
                            }, placeholder: placeholder, IconComponent: SelectIcon, css: native && shrinkLabel && value === '' ? classes.nativeHiddenPlaceholder : undefined, children: [placeholder ? (native ? (_jsx("option", { disabled: true, value: '', children: placeholder })) : (_jsx(MenuItem, { css: classes.menuItem, disabled: true, value: '', children: _jsx("em", { children: placeholder }) }))) : native && !multiple ? (_jsx("option", { disabled: true, hidden: true, value: '' })) : null, groupedItems] })] }), !shrinkLabel && !hiddenLabel ? (_jsx(TooltipLabel, { id: `${id}-input-label`, htmlFor: id, required: required, disabled: disabled, tooltipText: tooltipText, label: label, color: labelColor, css: { marginTop: '1rem' }, weight: weight })) : null, helperText ? (_jsx(FormHelperText, { disabled: disabled, css: classes.helperText(helperTextColor), children: helperText })) : null] })) }));
}
